import React from 'react'
import Parcelle from 'entity/Parcelle'
import { calculBounds } from 'utils/calculBounds'
import { selectParcelle } from 'redux/features/parcelle/slice'
import { useAppDispatch, useAppSelector } from 'redux/hooks'
import useFavori from 'hooks/useFavori'
import InformationList from 'components/InformationList'
import ButtonFavorite from 'components/ButtonFavorite'
import { Button } from 'react-bootstrap'
import googlePegman from 'assets/default/png/google_pegman.png'
import useGoogleMap from 'hooks/useGoogleMap'
import { makeSelectSelectedParcelleIndex } from 'redux/features/parcelle/selectors'
import { checkParcelleVue } from 'utils/parcellesVues'
import geoJsonObjectToCoordinates from 'utils/geoJsonObjectToCoordinates'
import cn from 'classnames'
import Card from '../Card'
import InformationSimple from './components/InformationSimple'
import InformationMulti from './components/InformationMulti'
import cardStyle from '../Card.module.scss'
import s from './CardParcelle.module.scss'
import TruncatedCommentaire from '../components/TruncatedCommentaire'
import BtnModalUrlAnnonceCommentaire from '../components/BtnModalUrlAnnonceCommentaire'

interface Props {
  parcelle: Parcelle
  onClick?: () => void
  count?: {
    index: number
    total: number
  }
}

const defaultProps = {
  onClick: () => null,
  count: undefined,
}

const CardParcelle = ({ parcelle, onClick, count }: Props): JSX.Element => {
  const selectedParcelleIndex = useAppSelector(makeSelectSelectedParcelleIndex)
  const { uid, geographie, parcelleTitle, parcelleType, centre } = parcelle
  const { active, commentaire } = useFavori(uid)
  const map = useGoogleMap()
  const dispatch = useAppDispatch()
  const releaseToggleV2 = process?.env?.REACT_APP_RELEASE_TOGGLE_V2 === 'true'
  const vue = checkParcelleVue(uid)

  const handleClick = () => {
    const bounds = calculBounds(geoJsonObjectToCoordinates(geographie))
    map?.fitBounds(bounds)
    map?.panToBounds(bounds)
    dispatch(selectParcelle(uid))
    if (onClick) onClick()
  }

  const setStreetView = () => {
    const targetPosition = new google.maps.LatLng({
      lat: centre.latitude,
      lng: centre.longitude,
    })
    const webService = new google.maps.StreetViewService()

    // On demande au webService de trouver un panorama
    webService
      .getPanorama({ location: targetPosition })
      .then((value) => {
        const closestStreetView = value.data.location?.latLng
        // Si il arrive à trouver un panorama, on prends la position qu'il nous donne
        if (closestStreetView !== undefined && closestStreetView !== null) {
          map?.getStreetView().setOptions({
            position: targetPosition,
            pov: {
              // Et on calcule vers où regarder
              heading: google.maps.geometry.spherical.computeHeading(
                closestStreetView,
                targetPosition
              ),
              pitch: 0,
            },
            visible: true,
          })
        } else {
          map?.getStreetView().setOptions({
            position: targetPosition,
            visible: true,
          })
        }
      })
      .catch(() => {
        map?.getStreetView().setVisible(false)
      })
  }

  return (
    <Card
      className={cn(s.cardParcelle, {
        [s.vue]: releaseToggleV2 && vue,
      })}
      selected={
        selectedParcelleIndex !== null && count?.index === selectedParcelleIndex
      }
      onClick={handleClick}
    >
      <Card.Header>
        <Card.Title>{parcelleTitle}</Card.Title>
        <Card.Badge>
          <ButtonFavorite uid={uid} />
        </Card.Badge>
      </Card.Header>
      {active && (
        <div className={s.urlAnnonceControls}>
          <BtnModalUrlAnnonceCommentaire uid={uid} />
        </div>
      )}
      <InformationList parcelle={parcelle} />
      <TruncatedCommentaire commentaire={commentaire} />
      <Button
        className={s.streetViewLink}
        onClick={setStreetView}
        variant="link"
      >
        <span>Aperçu Google Street View</span>{' '}
        <img src={googlePegman} alt="Pegman" />
      </Button>
      <div className={s.footer}>
        {parcelleType === 'simple' && <InformationSimple parcelle={parcelle} />}
        {parcelleType === 'multi' && <InformationMulti parcelle={parcelle} />}
        {releaseToggleV2 && vue && (
          <div className={cn(s.badgeVue, cardStyle.subTitle)}>
            <span>Déjà consulté</span>
          </div>
        )}
        {!releaseToggleV2 && count && (
          <div className={cardStyle.cardCount}>
            {`${count.index + 1} sur ${count.total}`}
          </div>
        )}
      </div>
    </Card>
  )
}

CardParcelle.defaultProps = defaultProps
export default CardParcelle
